import styled from 'styled-components';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { API_LIMITED_ACCESS, API_NO_ACCESS } from 'self-service-api/constants/FreeUserAccessValues';
import HubMap from 'self-service-api/constants/HubMap';
import { productLineToHub } from 'self-service-api/constants/ProductLineToHub';
import * as PurchaseTypes from 'self-service-api/constants/PurchaseTypes';
import { Flex } from 'foundations-components/components/Flex';
import { useMemo } from 'react';
import { getProductLevelFromApiNameString, getProductLineFromApiNameString } from '../_core/features/FeaturesUtils';
import FeatureTableCell from './FeatureTableCell';
import FeatureTableUserIcon from './FeatureTableUserIcon';
import TranslatedFeature from './TranslatedFeature';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledFeatureRow = styled.tr.withConfig({
  displayName: "FeatureRow__StyledFeatureRow"
})(({
  theme,
  rowIsVisible
}) => {
  return {
    borderBottom: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`,
    visibility: rowIsVisible ? 'visible' : 'hidden',
    display: 'table',
    width: '100%',
    tableLayout: 'fixed'
  };
});
const StyledHeaderCell = styled.th.withConfig({
  displayName: "FeatureRow__StyledHeaderCell"
})(({
  theme,
  isInbound25PricingPage
}) => {
  return {
    paddingRight: theme.space['300'],
    paddingTop: theme.space['300'],
    paddingBottom: theme.space['300'],
    paddingLeft: isInbound25PricingPage ? theme.space['300'] : undefined,
    textAlign: 'left'
  };
});
const TranslatedFeatureWrapper = styled.div.withConfig({
  displayName: "FeatureRow__TranslatedFeatureWrapper"
})(({
  theme
}) => {
  return {
    fontWeight: theme.typography['body-100'].fontWeight,
    textDecorationLine: 'underline',
    textDecorationStyle: 'dashed',
    textUnderlinePosition: 'under',
    textDecorationThickness: '1px'
  };
});
const FeatureRow = ({
  feature,
  apiNames,
  canViewNewSeats,
  rowIsVisible,
  isInbound25PricingPage = false,
  productLevels
}) => {
  const API_NAMES_WITHOUT_PRODUCT_LINES = ['crm-suite-starter-bundle', 'hubspot-free', 'breeze-intelligence-credits-100', 'breeze-intelligence-credits-1000', 'breeze-intelligence-credits-10000', 'core-seats-free',
  // doesn't exist in merchandise/products response, but does exist in feature store
  'core-seats-starter', 'core-seats-professional', 'core-seats-enterprise'];
  const featureProductLinesFromApiNames = feature.productApiNames.map(apiName => {
    if (API_NAMES_WITHOUT_PRODUCT_LINES.includes(apiName)) {
      return undefined;
    }
    try {
      return getProductLineFromApiNameString(apiName);
    } catch (error) {
      return undefined;
    }
  });
  const productLine = featureProductLinesFromApiNames.find(localProductLine => {
    if (localProductLine && HubMap[productLineToHub[localProductLine]].merchPurchaseType === PurchaseTypes.SEAT_BASED) {
      return localProductLine;
    }
    return false;
  });
  const showUserIcon = productLine !== undefined && (feature.freeUserAccessLevel === API_LIMITED_ACCESS || feature.freeUserAccessLevel === API_NO_ACCESS);
  const useProductLevels = productLevels && productLevels.length > 0;
  const productApiNames = useMemo(() => {
    if (!useProductLevels) {
      return apiNames || [];
    }
    return productLevels.map(productLevel => {
      return apiNames.find(productApiName => {
        const productLevelFromApiName = getProductLevelFromApiNameString(productApiName);
        return productLevelFromApiName === productLevel;
      });
    });
  }, [useProductLevels, productLevels, apiNames]);
  return /*#__PURE__*/_jsxs(StyledFeatureRow, {
    rowIsVisible: rowIsVisible,
    children: [/*#__PURE__*/_jsx(StyledHeaderCell, {
      isInbound25PricingPage: isInbound25PricingPage,
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        children: [showUserIcon && /*#__PURE__*/_jsx(Flex, {
          marginInlineEnd: "100",
          children: /*#__PURE__*/_jsx(FeatureTableUserIcon, {
            freeUserAccess: feature.freeUserAccessLevel,
            productLine: productLine,
            canViewNewSeats: canViewNewSeats
          })
        }), /*#__PURE__*/_jsx(TranslatedFeatureWrapper, {
          tabIndex: -1,
          children: /*#__PURE__*/_jsx(Tooltip, {
            use: "longform",
            title: /*#__PURE__*/_jsx(TranslatedFeature, {
              feature: feature,
              attribute: "description"
            }),
            children: /*#__PURE__*/_jsx("div", {
              children: /*#__PURE__*/_jsx(TranslatedFeature, {
                feature: feature,
                attribute: "name"
              })
            })
          })
        })]
      })
    }), productApiNames.map(apiName => /*#__PURE__*/_jsx(FeatureTableCell, {
      feature: feature,
      apiName: apiName
    }, `${feature.featureId}-${feature.displayName}-${apiName}`))]
  }, feature.featureId);
};
export default FeatureRow;